import { useCallback, useContext, useEffect, useRef } from 'react';
import type { Navigator, To } from 'react-router-dom';
import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';

interface Transition {
  retry: () => void;
}

// The `history` v5 browser history (NH web's router) can block navigations
type BlockingNavigator = Navigator & {
  block: (blocker: (transition: Transition) => void) => () => void;
};

function isBlockingNavigator(
  navigator: Navigator
): navigator is BlockingNavigator {
  return 'block' in navigator && typeof navigator.block === 'function';
}

export function usePrompt(
  message: string,
  when = true
): {
  unblock: () => void;
} {
  const { navigator } = useContext(NavigationContext);
  const canBlock = isBlockingNavigator(navigator);
  const unblockRef = useRef<(() => void) | undefined>(undefined);

  // Block links, navigate(), back/forward and tab close in one place
  useEffect(() => {
    if (!when || !isBlockingNavigator(navigator)) {
      return undefined;
    }

    const block = (): (() => void) =>
      navigator.block((transition) => {
        if (window.confirm(message)) {
          unblock();
          transition.retry();
          // The page may stay mounted (e.g. a query change), so keep guarding it
          unblock = block();
          unblockRef.current = unblock;
        }
      });
    let unblock = block();
    unblockRef.current = unblock;

    return () => {
      unblock();
      unblockRef.current = undefined;
    };
  }, [navigator, when, message]);

  usePopStatePrompt(message, when && !canBlock);

  const unblock = useCallback(() => {
    unblockRef.current?.();
    unblockRef.current = undefined;
  }, []);

  return { unblock };
}

// navigate() that reports whether it went through: it does not when the user
// cancels a usePrompt confirmation
export function useTryNavigate(): (to: To) => boolean {
  const navigate = useNavigate();
  const { navigator } = useContext(NavigationContext);

  return useCallback(
    (to: To) => {
      const before = currentLocation(navigator);
      navigate(to);
      return before === undefined || currentLocation(navigator) !== before;
    },
    [navigate, navigator]
  );
}

function currentLocation(navigator: Navigator): unknown {
  return 'location' in navigator ? navigator.location : undefined;
}

// Fallback for routers without `block` (react-router's BrowserRouter):
// only catches the back button and tab close
function usePopStatePrompt(message: string, when: boolean): void {
  const location = useLocation();

  // Block browser/tab close with beforeunload
  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Safari and older browsers only prompt when returnValue is set
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // Block in-app navigation by intercepting popstate
  useEffect(() => {
    if (!when) {
      return undefined;
    }

    // Push a duplicate entry so we can intercept the back button
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (window.confirm(message)) {
        // User confirmed — go back for real
        window.history.back();
      } else {
        // User cancelled — re-push to keep them on the page
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [when, message, location]);
}
